import { pickT } from "@repo/shared";
import { VisualizationContainer } from "@/components/flow/visualization-container";
import { useInstanceCostSnapshot } from "@/lib/hooks/use-flow-instances/use-goal-snapshots";
import { CostChartWidget } from "../cost-chart-widget";
import { T } from "./global-cost-chart.i18n";

type GlobalCostChartProps = {
  instanceId: string;
  config: Record<string, unknown>;
  language: string;
  /**
   * Phase 2.5 D36 — engine reported `kind: "snapshot"` for this component (no
   * goalId, snapshotType="instance_cost"). When set AND the snapshot row is
   * missing, replace the muted "Cost data is not available" fallback with a
   * red-bordered placeholder pointing at the regenerate banner.
   */
  hasValidationError?: boolean;
};

export function GlobalCostChart({
  instanceId,
  config,
  language,
  hasValidationError = false,
}: GlobalCostChartProps) {
  const t = pickT(T, language);
  const { data, isLoading } = useInstanceCostSnapshot(instanceId);

  if (isLoading) return null;

  if (!data) {
    if (hasValidationError) {
      return (
        <div className="text-sm text-destructive border border-destructive rounded-lg p-4">
          {t.snapshotMissingMessage}
        </div>
      );
    }
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.notAvailable}
      </div>
    );
  }

  const snapshot =
    typeof data === "string"
      ? (() => {
          try {
            return JSON.parse(data as string);
          } catch {
            return null;
          }
        })()
      : data;

  if (!snapshot) {
    return <div className="text-sm text-destructive">{t.errorLoading}</div>;
  }

  if (snapshot.error) {
    return (
      <div className="border border-destructive/50 rounded-lg p-4 space-y-2">
        <div className="text-sm font-medium text-destructive">{t.unavailable}</div>
        <div className="text-xs text-muted-foreground">{t.contactAdvisor}</div>
      </div>
    );
  }

  return (
    <VisualizationContainer config={config}>
      <CostChartWidget
        snapshot={snapshot}
        displayMode={(config.displayMode as "rolling" | "calendar") || undefined}
        chartType={(config.chartType as "line" | "bar") || undefined}
        projectionYears={(config.projectionYears as number) || undefined}
        language={language}
      />
    </VisualizationContainer>
  );
}
